// Email reminder subscriptions — feature logic (docs/reminders.md).
//
// The API primitives live in data.ts; this module owns the role-derived option
// lists, the derived display status, and form validation. Only steps the user
// can act on are offered, and the digest itself is counts and dollar totals
// only — no vendor, no invoice number, no participant data.
//
// Two things the port changed. Editing is copy-on-write, and that is now ONE
// server call (`replaceReminder`): the old client created the replacement, then
// superseded the original, and had to retry the second write and warn about a
// possible duplicate when it failed. And the day, workspace and step lists are
// real arrays on the wire instead of comma-joined strings.
import { TIMELINE_NODES, WORKSPACES, type NodeKey, type Workspace } from './constants'
import { scopeSession, availableWorkspaces, type Session } from './identity'
import { createReminder, replaceReminder, setReminderStatus } from './data'
import type { ReminderSubscription, ReminderWrite } from './types'

// Day tokens the scheduler matches against (`ddd` in Eastern time).
export const DAY_TOKENS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
export type DayToken = (typeof DAY_TOKENS)[number]

// Whole-hour options — the scheduler ticks hourly, so that is the finest
// granularity, and the form must not imply minute precision.
export const HOUR_OPTIONS: { value: string; label: string }[] = Array.from({ length: 24 }, (_, h) => ({
  value: `${String(h).padStart(2, '0')}:00`,
  label: h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`,
}))

/** `HH:MM`, dropping any seconds the wire carries, so a stored `09:00:00`
    still matches an option. */
export function normalizeHour(time: string | undefined | null): string {
  return (time ?? '').slice(0, 5)
}

export function hourLabel(time: string | undefined): string {
  const t = normalizeHour(time)
  return HOUR_OPTIONS.find((o) => o.value === t)?.label ?? t
}

/** Today in the browser's local zone (Eastern for these users) as yyyy-MM-dd —
    matches the string dates stored on the row. */
export function todayLocal(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// ---- Options derived from the user's roles ---------------------------------

export interface StepOption {
  key: NodeKey
  label: string
  /** Workspaces (of the user's available set) where this step is actionable. */
  workspaces: Workspace[]
}

/** Pipeline steps the user can act on, unioned across their available
    workspaces (Archives excluded — it is a lookup, not a waiting queue). The
    step keys are the hyphenated node keys, which are exactly the tokens the
    subscription stores. */
export function stepOptions(session: Session): StepOption[] {
  const byKey = new Map<NodeKey, StepOption>()
  for (const ws of availableWorkspaces(session)) {
    const roles = new Set(scopeSession(session, ws).roles)
    for (const n of TIMELINE_NODES) {
      if (n.archive) continue
      if (!roles.has(n.role)) continue
      const existing = byKey.get(n.key)
      if (existing) existing.workspaces.push(ws)
      else byKey.set(n.key, { key: n.key, label: n.label, workspaces: [ws] })
    }
  }
  return TIMELINE_NODES.filter((n) => byKey.has(n.key)).map((n) => byKey.get(n.key)!)
}

// ---- Display status (derived, never stored) --------------------------------
export type DisplayStatus = 'Scheduled' | 'Paused' | 'Not started' | 'Ended'

export function deriveDisplayStatus(row: ReminderSubscription, today = todayLocal()): DisplayStatus {
  if (row.status === 'paused') return 'Paused'
  const start = row.start_date ?? ''
  const end = row.end_date ?? ''
  if (start && start > today) return 'Not started'
  if (end && end < today) return 'Ended'
  return 'Scheduled'
}

export function formatSchedule(row: ReminderSubscription): string {
  const days = (row.days_of_week ?? []).filter(Boolean).join(', ') || 'No days'
  const range = row.start_date
    ? row.end_date
      ? `${row.start_date} – ${row.end_date}`
      : `from ${row.start_date}`
    : ''
  return `${days} at ${hourLabel(row.time_of_day)}${range ? ` · ${range}` : ''}`
}

// ---- Form model ------------------------------------------------------------
export interface ReminderForm {
  label: string
  days: string[]
  startDate: string
  endDate: string // '' = open-ended
  time: string
  /** Fixed to the one workspace. The picker is gone (a one-option control), but
      the value is still submitted — the API requires at least one. */
  workspaces: Workspace[]
  steps: NodeKey[]
}

export function emptyForm(): ReminderForm {
  return {
    label: '',
    days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    startDate: todayLocal(),
    endDate: '',
    time: '09:00',
    workspaces: [...WORKSPACES],
    steps: [],
  }
}

export function rowToForm(row: ReminderSubscription): ReminderForm {
  const stored = (row.workspaces ?? []).filter((w): w is Workspace =>
    (WORKSPACES as readonly string[]).includes(w),
  )
  return {
    label: row.label ?? '',
    days: [...(row.days_of_week ?? [])],
    startDate: row.start_date ?? '',
    endDate: row.end_date ?? '',
    time: normalizeHour(row.time_of_day) || '09:00',
    workspaces: stored.length > 0 ? stored : [...WORKSPACES],
    steps: [...(row.steps ?? [])] as NodeKey[],
  }
}

export function validateForm(form: ReminderForm): string | null {
  if (form.workspaces.length === 0) return 'Choose at least one document type.'
  if (form.steps.length === 0) return 'Choose at least one step.'
  if (form.days.length === 0) return 'Choose at least one day of the week.'
  if (!form.startDate) return 'Choose a start date.'
  if (!form.time) return 'Choose a time of day.'
  // The scheduler compares the hour only, so a half-past time would never fire
  // at the minute it names. The API rejects it too (400); this is the friendlier
  // version of the same rule.
  if (!/^\d{2}:00$/.test(normalizeHour(form.time))) return 'Pick a whole hour — reminders are sent hourly.'
  if (form.endDate && form.endDate < form.startDate) return 'The end date must be on or after the start date.'
  return null
}

function formToWrite(form: ReminderForm): ReminderWrite {
  return {
    label: form.label.trim() || null,
    days_of_week: form.days,
    start_date: form.startDate,
    // Open-ended must be null, never '' — the scheduler treats null as "no end".
    end_date: form.endDate ? form.endDate : null,
    time_of_day: normalizeHour(form.time),
    workspaces: form.workspaces,
    steps: form.steps,
  }
}

// ---- Writes ----------------------------------------------------------------
// The server owns the subscriber (it knows the caller), the lineage, and the
// last-sent stamp carried across an edit so a mid-day change cannot double-send.

export async function saveNewSubscription(form: ReminderForm): Promise<void> {
  await createReminder(formToWrite(form))
}

/** Editing is copy-on-write, in one server transaction: the replacement is
    written and the original superseded together, so a failure leaves the old
    version live rather than losing both. */
export async function saveEditedSubscription(old: ReminderSubscription, form: ReminderForm): Promise<void> {
  await replaceReminder(old.id, { ...formToWrite(form), row_version: old.row_version })
}

export async function setSubscriptionPaused(row: ReminderSubscription, paused: boolean): Promise<void> {
  await setReminderStatus(row.id, paused ? 'paused' : 'active')
}

export async function cancelSubscription(row: ReminderSubscription): Promise<void> {
  await setReminderStatus(row.id, 'cancelled')
}
